import { useEffect, useState } from 'react'
import { X, FlaskConical, CheckCircle2, XCircle, Loader2, Plus, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { kioskApi } from './kiosk-api'
import KioskNumpad from './KioskNumpad'

type LoteAguardando = {
  id: number; codigo: string; op_numero: number | null
  produto_codigo: string | null; produto_descricao: string | null
  horas_cura: number; exige_laudo_qc: boolean
  cura_fim_previsto: string | null; atrasado: boolean
}

type Medicao = {
  parametro: string; label: string; valor: string;
  unidade: string; min: string; max: string;
}

const PRESETS: Medicao[] = [
  { parametro: 'shore_a', label: 'Dureza Shore A', valor: '', unidade: 'Sh A', min: '60', max: '75' },
  { parametro: 'densidade', label: 'Densidade', valor: '', unidade: 'g/cm³', min: '1.10', max: '1.30' },
  { parametro: 'abrasao', label: 'Abrasão', valor: '', unidade: 'mm³', min: '0', max: '200' },
  { parametro: 'alongamento', label: 'Alongamento ruptura', valor: '', unidade: '%', min: '300', max: '700' },
]

export default function KioskLaudo({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<'lista' | 'editar'>('lista')
  const [lotes, setLotes] = useState<LoteAguardando[]>([])
  const [loteAtivo, setLoteAtivo] = useState<LoteAguardando | null>(null)
  const [medicoes, setMedicoes] = useState<Medicao[]>([])
  const [campoFoco, setCampoFoco] = useState<{ idx: number; campo: 'valor' | 'min' | 'max' } | null>(null)
  const [conclusao, setConclusao] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (step !== 'lista') return
    kioskApi.get<LoteAguardando[]>('/kiosk/lotes-aguardando').then(r => setLotes(r.data))
      .catch(() => toast.error('Erro ao carregar lotes'))
  }, [step])

  function abrir(lote: LoteAguardando) {
    setLoteAtivo(lote)
    setMedicoes(PRESETS.map(p => ({ ...p })))
    setConclusao('')
    setStep('editar')
  }

  function atualizaMed(idx: number, campo: keyof Medicao, valor: string) {
    setMedicoes(m => m.map((med, i) => i === idx ? { ...med, [campo]: valor } : med))
  }

  function adicionarLinha() {
    setMedicoes(m => [...m, { parametro: 'outro', label: 'Outro', valor: '', unidade: '', min: '', max: '' }])
  }

  function removerLinha(idx: number) {
    setMedicoes(m => m.filter((_, i) => i !== idx))
  }

  async function salvarLaudo(aprovar: boolean) {
    if (!loteAtivo) return
    const medsValidas = medicoes.filter(m => m.valor !== '')
    if (medsValidas.length === 0) {
      toast.error('Preencha ao menos uma medição')
      return
    }
    setLoading(true)
    try {
      const r = await kioskApi.post(`/kiosk/lotes/${loteAtivo.id}/laudo`, {
        medicoes: medsValidas.map(m => ({
          parametro: m.parametro, label: m.label, unidade: m.unidade,
          valor: parseFloat(m.valor),
          min: m.min ? parseFloat(m.min) : null,
          max: m.max ? parseFloat(m.max) : null,
        })),
        conclusao: conclusao || undefined,
        decisao: aprovar ? 'aprovar' : 'reprovar',
      })
      toast.success(aprovar ? `Lote ${loteAtivo.codigo} liberado` : `Lote ${loteAtivo.codigo} bloqueado`)
      setStep('lista')
      setLoteAtivo(null)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    } finally { setLoading(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center">
      <div className="bg-white w-full md:max-w-2xl rounded-t-2xl md:rounded-2xl p-5 max-h-[95vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FlaskConical className="h-6 w-6 text-violet-600" /> Laudo QC
            {step === 'editar' && loteAtivo && (
              <span className="text-sm font-normal text-ink-500">— {loteAtivo.codigo}</span>
            )}
          </h2>
          <button onClick={onClose}><X className="h-6 w-6" /></button>
        </div>

        {step === 'lista' && (
          <>
            <p className="text-sm text-ink-600 mb-3">Lotes aguardando análise laboratorial:</p>
            {lotes.length === 0 && (
              <div className="bg-emerald-50 rounded-xl p-6 text-center text-emerald-800">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2" />
                Nenhum lote aguardando QC.
              </div>
            )}
            <div className="space-y-2">
              {lotes.map(l => (
                <button
                  key={l.id}
                  onClick={() => abrir(l)}
                  className={`w-full text-left p-3 rounded-xl border-2 hover:border-violet-400 ${l.atrasado ? 'border-red-300 bg-red-50' : 'border-ink-200'}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-mono text-xs text-ink-500">{l.codigo}</div>
                      <div className="font-semibold">{l.produto_descricao}</div>
                      {l.op_numero && <div className="text-xs text-violet-700">OP #{l.op_numero}</div>}
                    </div>
                    {l.atrasado && <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded font-bold">ATRASADO</span>}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 'editar' && loteAtivo && (
          <>
            <div className="space-y-3 mb-4">
              {medicoes.map((m, idx) => (
                <div key={idx} className="bg-ink-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      value={m.label}
                      onChange={(e) => atualizaMed(idx, 'label', e.target.value)}
                      className="flex-1 px-2 py-1 border border-ink-200 rounded text-sm font-medium"
                    />
                    <input
                      value={m.unidade}
                      onChange={(e) => atualizaMed(idx, 'unidade', e.target.value)}
                      placeholder="un"
                      className="w-20 px-2 py-1 border border-ink-200 rounded text-sm"
                    />
                    <button onClick={() => removerLinha(idx)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setCampoFoco({ idx, campo: 'min' })}
                      className={`h-12 rounded-lg text-sm border-2 ${campoFoco?.idx === idx && campoFoco?.campo === 'min' ? 'border-violet-500 bg-violet-50' : 'border-ink-200 bg-white'}`}
                    >
                      <div className="text-xs uppercase text-ink-500">Mín</div>
                      <div className="font-bold">{m.min || '—'}</div>
                    </button>
                    <button
                      onClick={() => setCampoFoco({ idx, campo: 'valor' })}
                      className={`h-12 rounded-lg text-sm border-2 font-bold ${campoFoco?.idx === idx && campoFoco?.campo === 'valor' ? 'border-violet-500 bg-violet-50' : 'border-ink-200 bg-white'}`}
                    >
                      <div className="text-xs uppercase text-ink-500">Medido</div>
                      <div className="text-base">{m.valor || '—'}</div>
                    </button>
                    <button
                      onClick={() => setCampoFoco({ idx, campo: 'max' })}
                      className={`h-12 rounded-lg text-sm border-2 ${campoFoco?.idx === idx && campoFoco?.campo === 'max' ? 'border-violet-500 bg-violet-50' : 'border-ink-200 bg-white'}`}
                    >
                      <div className="text-xs uppercase text-ink-500">Máx</div>
                      <div className="font-bold">{m.max || '—'}</div>
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={adicionarLinha}
                className="w-full h-10 border-2 border-dashed border-ink-300 hover:border-violet-400 rounded-lg flex items-center justify-center gap-2 text-sm text-ink-600"
              >
                <Plus className="h-4 w-4" /> Adicionar parâmetro
              </button>
            </div>

            {campoFoco && (
              <div className="border-t border-ink-200 pt-3 mb-3">
                <div className="text-xs uppercase text-ink-500 font-semibold mb-2">
                  Editando: {medicoes[campoFoco.idx]?.label} → {campoFoco.campo}
                </div>
                <KioskNumpad
                  value={medicoes[campoFoco.idx]?.[campoFoco.campo] || ''}
                  onChange={(v) => atualizaMed(campoFoco.idx, campoFoco.campo, v)}
                  allowDecimal maxLen={8}
                />
              </div>
            )}

            <textarea
              value={conclusao}
              onChange={(e) => setConclusao(e.target.value)}
              placeholder="Observação / conclusão"
              rows={2}
              className="w-full px-3 py-2 border-2 border-ink-200 rounded-lg text-sm mb-3"
            />

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => salvarLaudo(false)}
                disabled={loading}
                className="h-14 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <XCircle className="h-5 w-5" />}
                Reprovar
              </button>
              <button
                onClick={() => salvarLaudo(true)}
                disabled={loading}
                className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                Aprovar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
